import type { UsDiagnostic } from '../api/userStories';
import type { MessageKey } from '../i18n';

/**
 * What this util needs from i18n — nothing more.
 *
 * The import is TYPE-ONLY, so it is erased at compile time and the module stays a runtime leaf
 * (which is what lets the tests import it directly). Typing the key as `MessageKey` rather than
 * `string` is the point: a mistyped key then fails to compile instead of printing itself on a
 * diagnostic panel.
 */
type Translate = (key: MessageKey, values?: Record<string, unknown>) => string;

/**
 * Why no user story is shown, derived from the FACTS the server serves.
 *
 * ⚠️ EACH BRANCH IS A DISTINCT CAUSE, AND THEY DO NOT SEND YOU TO THE SAME PLACE:
 * a storyline with no user story is repaired in the studio, a stopped engine is restarted from
 * the back-office, a missing licence is an enrolment matter. Confusing them — which the single
 * message this replaced did — sends you searching for hours on the side that has nothing to do
 * with it.
 *
 * With no facts, we say we do not know. That is the only honest fallback.
 *
 * ⚠️ THE TRANSLATE FUNCTION IS INJECTED, NOT IMPORTED — and that is deliberate on two counts.
 * It keeps this module a LEAF, which is the only reason the tests can import it directly and pin
 * every branch. And it forbids reading a language from global state: a util that did so would
 * render whichever language the module happened to see first, the same freeze that bit the
 * module-level constants in CompanyDetailScreen.
 */
export function missingUsReason(d: UsDiagnostic | null | undefined, t: Translate): string {
  if (!d || d.authored == null) return t('usDiag.unknown');
  if (d.authored === 0) return t('usDiag.noneAuthored');
  // ⚠️ THE LICENCE ONLY LOCKS A NON-PUBLIC MISSION (B2C freemium is licence-free). Naming it on
  // a public one accuses the one lock that cannot be the cause, and hides the real one.
  if (d.license === false && d.public !== true) return t('usDiag.noLicense', { count: d.authored });
  if (d.engine !== 'active') {
    const state = d.engine
      ? t('usDiag.engineStopped', { status: d.engine })
      : t('usDiag.engineNeverStarted');
    return t('usDiag.engineDown', { count: d.authored, state });
  }
  return t('usDiag.allGood', { count: d.authored });
}
